import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { toast, ToastContainer } from "react-toastify";
import { loadOffices, OfficeLocation } from "../services/platformSource";
import { loadSection } from "../services/contentSource";
import "react-toastify/dist/ReactToastify.css";
import ReCaptchaCheckbox from "../utils/ReCaptchaCheckbox";
import { postContact } from "../services/api";
import Seo from "../components/Seo";
import pageFallback from "../data/contactPage.json";

const Contact = () => {
  const [token, setToken] = useState<string | null>(null);
  // Same CMS source as the footer, so opening an office updates both.
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [page, setPage] = useState<any>(pageFallback);

  useEffect(() => {
    let cancelled = false;
    loadOffices().then((rows) => {
      if (!cancelled) setOffices(rows);
    });
    loadSection("contactPage", pageFallback as any).then(({ data: main }) => {
      if (!cancelled && main) setPage(main);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    messages: "",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name.toLowerCase()]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Checked before the submitting flag is set: returning early after
    // setIsSubmitting(true) left the button disabled until a page reload, on
    // the one page whose whole job is conversion.
    if (!token) {
      toast.error("Please complete the reCAPTCHA check.");
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await postContact({ ...formData, token: token || "" });

      if (data.success) {
        setShowSuccess(true);
        setIsSubmitting(false);
        setTimeout(() => {
          setShowSuccess(false);
          setFormData({ name: "", email: "", phone: "", subject: "", messages: "" });
        }, 5000);
      } else {
        toast.error("Failed to send message. Please try again.");
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
      setIsSubmitting(false);
      console.error(error);
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Talk to the engineers who built the systems. Book a demo or ask a question."
        path="/contact"
      />
      <link rel="stylesheet" href="/assets/css/master-contact.css" />
      <main>
        {/* contact area start */}
        <section className="contact-area">
          <div className="container">
            <div className="contact-area-inner section-spacing">
              <div className="section-header">
                <div className="section-title-wrapper">
                  <div className="title-wrapper">
                    <h2 className="section-title large has_fade_anim">
                      {page.title}
                    </h2>
                  </div>
                </div>
                <div className="text-wrapper">
                  <p className="text has_fade_anim">
                    {page.lede}
                  </p>
                </div>
              </div>
              <div className="section-content">
                <div className="info-box has_fade_anim">
                  {/* Contact details as readable text, not bare icons. The
                      previous version rendered a phone, an envelope and a
                      WhatsApp glyph with nothing but aria-labels — a sighted
                      visitor could not read the number or the address without
                      clicking one to find out where it went. */}
                  <ul className="mx-contact-methods">
                    <li>
                      <span className="mx-eyebrow">Email</span>
                      <a href="mailto:hello@mindsyncx.com">hello@mindsyncx.com</a>
                    </li>
                    <li>
                      <span className="mx-eyebrow">Phone</span>
                      <a href="tel:+919429167971">+91 94291 67971</a>
                    </li>
                    <li>
                      <span className="mx-eyebrow">WhatsApp</span>
                      <a
                        href="https://wa.me/919429167971"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Message us <span aria-hidden="true">↗</span>
                      </a>
                    </li>
                  </ul>

                  <div className="mx-contact-note">
                    <p className="mx-eyebrow">What happens next</p>
                    <p>
                      One of the engineers reads it and replies, usually within a
                      working day. If you asked something specific you will get a
                      specific answer rather than a calendar link.
                    </p>
                  </div>

                  {offices.length > 0 && (
                    <div className="mx-contact-offices">
                      <p className="mx-eyebrow">Offices</p>
                      <ul>
                        {offices.map((office) => (
                          <li key={office.name}>
                            <strong>{office.name}</strong>
                            <span>{office.address}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="contact-wrap has_fade_anim" data-delay="0.30">
                  {showSuccess ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <h3>Thank you for choosing us!</h3>
                      <div style={{ fontSize: "48px", marginTop: "10px" }}>✅</div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="contact-formwrap">
                        <div className="contact-formfield">
                          <input
                            type="text"
                            name="name"
                            id="Name"
                            placeholder="Name*"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="contact-formfield">
                          <input
                            type="email"
                            name="email"
                            id="Email"
                            placeholder="Email*"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="contact-formfield">
                          <input
                            type="text"
                            name="phone"
                            id="Phone"
                            placeholder="Phone"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="contact-formfield">
                          <input
                            type="text"
                            name="subject"
                            id="Subject"
                            placeholder="Subject*"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div className="contact-formfield messages text-area">
                          <textarea
                            name="messages"
                            id="Messages"
                            placeholder="Message*"
                            value={formData.messages}
                            onChange={handleChange}
                            required
                            rows={5}
                          />
                        </div>
                      </div>
                      <div className="submit-btn">
                        {/* Reusable reCAPTCHA */}
                        <ReCaptchaCheckbox onChange={setToken} />
                        <button
                          type="submit"
                          className="wc-btn wc-btn-primary btn-text-flip"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span className="loading-spinner">Submitting...</span> // or spinner icon
                          ) : (
                            <span data-text="Submit">Submit</span>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* contact area end */}
        <ToastContainer position="top-center" autoClose={3000} />
      </main>
    </>
  );
};

export default Contact;
